import React from "react";
import Insurer from "app/assets/ReviewIcons/insurer_badge.png";
import HexagonDollarIcon from "app/assets/IconComponent/HexagonDollarIcon";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LedgerRow {
  insuredName: string;
  insuredInitials: string;
  insuredColor: string;
  policyNumber: string;
  type: string;
  benefit: number;
  expiration: string;
  method: string;
  mode: string;
  amount: number;
  annualized: number;
}

interface FundingLedgerProps {
  data?: LedgerRow[];
  isDark?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtCurrency = (v: number): string => {
  if (v >= 1_000_000) {
    return `$${(v / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 1 })}M`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
};

// ── Mock data ─────────────────────────────────────────────────────────────────

const MOCK_ROWS: LedgerRow[] = [
  {
    insuredName: "Tony Stark",
    insuredInitials: "TS",
    insuredColor: "#4F46E5",
    policyNumber: "L99887665",
    type: "Life",
    benefit: 5_200_000,
    expiration: "13 Yrs",
    method: "I",
    mode: "A",
    amount: 2500,
    annualized: 2500,
  },
  {
    insuredName: "John Smith",
    insuredInitials: "JS",
    insuredColor: "#7C3AED",
    policyNumber: "L99887665",
    type: "Life",
    benefit: 5_200_000,
    expiration: "13 Yrs",
    method: "I",
    mode: "A",
    amount: 2500,
    annualized: 2500,
  },
  {
    insuredName: "John Smith",
    insuredInitials: "JS",
    insuredColor: "#7C3AED",
    policyNumber: "L99887665",
    type: "Life",
    benefit: 5_200_000,
    expiration: "13 Yrs",
    method: "C",
    mode: "SA",
    amount: 1250,
    annualized: 2500,
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const Avatar = ({ initials, color }: { initials: string; color: string }) => (
  <div
    style={{
      width: 36,
      height: 36,
      borderRadius: "50%",
      backgroundColor: color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontSize: 12,
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    {initials}
  </div>
);

const InsurerBadge = () => (
  <div
    style={{
      width: 36,
      height: 36,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <img src={Insurer} alt="insurer" style={{ width: 26, height: 26, objectFit: "contain" }} />
    </div>
  </div>
);

const PolicyDot = () => (
  <span
    style={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#22C55E",
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

const AmountBadge = ({ value }: { value: number }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ fontWeight: 500 }}>{fmtCurrency(value)}</span>
    <div
      style={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        backgroundColor: "#22C55E",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <HexagonDollarIcon fill="white" width={14} height={16} />
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const HEADER_CLS = "text-[11px] font-semibold uppercase tracking-wider text-gray-400 text-left";
const CELL_CLS = "py-3 text-sm text-gray-700 dark:text-gray-200";

export default function FundingLedger({ data, isDark }: FundingLedgerProps) {
  const rows = data && data.length > 0 ? data : MOCK_ROWS;

  const totalBenefit = rows.reduce((s, r) => s + r.benefit, 0);
  const totalAnnualized = rows.reduce((s, r) => s + r.annualized, 0);

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const dividerColor = isDark ? "#3A3A50" : "#F3F4F6";

  return (
    <div className={`rounded-2xl shadow-md overflow-hidden ${cardBg} px-5 pt-4 pb-3`}>
      <div className="overflow-x-auto">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 750 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${dividerColor}` }}>
              <th className={HEADER_CLS} style={{ paddingRight: 12, paddingBottom: 10 }}>
                Insured
              </th>
              <th className={HEADER_CLS} style={{ paddingRight: 12, paddingBottom: 10 }}>
                Insurer
              </th>
              <th className={HEADER_CLS} style={{ paddingRight: 16, paddingBottom: 10 }}>
                Policy #
              </th>
              <th className={HEADER_CLS} style={{ paddingRight: 16, paddingBottom: 10 }}>
                Type
              </th>
              <th className={HEADER_CLS} style={{ paddingRight: 16, paddingBottom: 10 }}>
                Benefit
              </th>
              <th className={HEADER_CLS} style={{ paddingRight: 16, paddingBottom: 10 }}>
                Expiration
              </th>
              <th className={HEADER_CLS} style={{ paddingRight: 16, paddingBottom: 10 }}>
                Method
              </th>
              <th className={HEADER_CLS} style={{ paddingRight: 16, paddingBottom: 10 }}>
                Mode
              </th>
              <th className={HEADER_CLS} style={{ paddingRight: 16, paddingBottom: 10 }}>
                Amount
              </th>
              <th className={HEADER_CLS} style={{ paddingBottom: 10 }}>
                Annualized
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                style={{
                  borderBottom: i < rows.length - 1 ? `1px solid ${dividerColor}` : "none",
                }}
              >
                <td className={CELL_CLS} style={{ paddingRight: 12 }}>
                  <Avatar initials={row.insuredInitials} color={row.insuredColor} />
                </td>
                <td className={CELL_CLS} style={{ paddingRight: 12 }}>
                  <InsurerBadge />
                </td>
                <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <PolicyDot />
                    {row.policyNumber}
                  </div>
                </td>
                <td className={`${CELL_CLS} font-bold`} style={{ paddingRight: 16 }}>
                  {row.type}
                </td>
                <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                  {fmtCurrency(row.benefit)}
                </td>
                <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                  {row.expiration}
                </td>
                <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                  {row.method}
                </td>
                <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                  {row.mode}
                </td>
                <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                  <AmountBadge value={row.amount} />
                </td>
                <td className={CELL_CLS}>{fmtCurrency(row.annualized)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer totals */}
      <div
        style={{
          borderTop: `1px solid ${dividerColor}`,
          marginTop: 4,
          width: "75%",
          justifySelf: "right",
        }}
        className="flex items-center justify-between flex-wrap pt-3 pb-1 gap-4"
      >
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Total Benefit
          </span>
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {fmtCurrency(totalBenefit)}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Total Annualized Premium
          </span>
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {fmtCurrency(totalAnnualized)}
          </span>
        </div>
      </div>
    </div>
  );
}
